import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { CHILDREN, CLASSROOM, CONTENT_TYPE } from "../config/constants";

const CHILDREN_QUERY_KEY = ["get-children"];

const invalidateChildren = (queryClient, childId) => {
  queryClient.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: ["parent"] });
  queryClient.invalidateQueries({ queryKey: ["search-students"] });
  if (childId) queryClient.invalidateQueries({ queryKey: ["child", childId] });
};

export const useSearchParents = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["search-parents", params],
    queryFn: async () => {
      const { data } = await Get(CHILDREN.searchParents, token, params);
      return data.parents || [];
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetAllChildren = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...CHILDREN_QUERY_KEY, params],
    queryFn: async ({ signal }) => {
      const { data } = await Get(CHILDREN.getAllChildren, token, params, { signal });
      return data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useUpdateChildStatus = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ childId }) => {
      const body = await Get(CHILDREN.toggleStatus + childId, token);
      if (!body?.status) {
        throw new Error(body?.message || "Failed to update status");
      }
      return body;
    },
    onSuccess: (_data, variables) => {
      invalidateChildren(queryClient, variables?.childId);
    },
  });
};

export const useAddChild = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ data }) => {
      const response = await Post(CHILDREN.addChild, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to create student");
    },
    onSuccess: () => invalidateChildren(queryClient),
  });
};

export const useGetChildById = (childId) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["child", childId],
    queryFn: async () => {
      const response = await Get(CHILDREN.getChildById + childId, token);
      return response.data.child;
    },
    enabled: !!token && !!childId,
  });
};

export const useGetClassrooms = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["classrooms-for-children"],
    queryFn: async () => {
      const response = await Get(`${CLASSROOM.getAllClassrooms}`, token, { page: 1, limit: 100 });
      return response.data.docs || [];
    },
    enabled: !!token,
  });
};

export const useUpdateChild = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ childId, data }) => {
      const response = await Post(CHILDREN.updateChild + childId, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || response?.response?.data?.message || "Failed to update student");
    },
    onSuccess: (_data, variables) => {
      invalidateChildren(queryClient, variables?.childId);
    },
  });
};

export const useGetChildAttendanceByMonth = (params) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["child-attendance", params],
    queryFn: async () => {
      const { data } = await Get(CHILDREN.getChildAttendanceByMonth + params.id, token, params);
      return data.attendance || [];
    },
    enabled: !!token && !!params?.id,
  });
};

export const useMarkChildAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ childId, data }) => {
      const response = await Post(CHILDREN.markChildAttendance + childId, data, token);
      if (response?.data?.status === false) {
        throw new Error(response.data.message || "Failed to save attendance");
      }
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["child-attendance"] }),
  });
};

export const useUpdateChildAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ attendanceId, data }) => {
      const response = await Post(CHILDREN.updateChildAttendance + attendanceId, data, token);
      if (response?.data?.status === false) {
        throw new Error(response.data.message || "Failed to update attendance");
      }
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["child-attendance"] }),
  });
};

export const useDeleteChildAttendance = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (attendanceId) => {
      const response = await Post(CHILDREN.deleteChildAttendance + attendanceId, {}, token);
      if (response?.data?.status === false) {
        throw new Error(response.data.message || "Failed to delete attendance");
      }
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["child-attendance"] }),
  });
};

export const useDeleteChild = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (childId) => {
      const response = await Get(CHILDREN.deleteChild + childId, token);
      const body =
        typeof response?.status === "boolean" ? response : response?.response?.data || response?.data;
      if (!body?.status) {
        throw new Error(body?.message || "Failed to delete student");
      }
      return body;
    },
    onSuccess: () => invalidateChildren(queryClient),
  });
};
